import { FC } from 'react';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { TransactionV2 } from '../invoices.model';
import { ReactComponent as ChevronArrowRightIcon } from 'assets/icons/ChevronArrowRightIcon.svg';
import { getPaymentMethodNameForTxnCard, getTxnCardIcon } from 'utils/transactionUtils';
import { TransactionStatus, TransactionType } from '../constants';
import { formatTransactionDate, getCardType, getReceiptStatus, getReceiptStatusColor } from 'containers/receipts/util';
import { DISPUTE_STATUS } from 'containers/disputes/constants';
import { PaymentMethodKeyNameLookup } from 'constants/common';

interface TransactionCardV2Props {
  transaction: TransactionV2; // pass paymentMethodName to map the icon
  onTxnClick: (id: string, status?: string) => void;
  onDisputeClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  shouldCheckPermission: boolean;
}

const TransactionCardV2: FC<TransactionCardV2Props> = ({
  transaction,
  onTxnClick,
  onDisputeClick,
  onRefundClick,
  shouldCheckPermission = true
}) => {
  const hasPermission = shouldCheckPermission ? transaction.hasPermission : true;

  const handleCardClick = () => {
    if (!hasPermission) {
      return;
    }
    if (transaction.type === TransactionType.DISPUTE && onDisputeClick) {
      onDisputeClick(transaction.disputeId);
    } else if (transaction.type === TransactionType.REFUND && transaction.refundTransactionId && onRefundClick) {
      onRefundClick(transaction.refundTransactionId);
    } else if (transaction.type === TransactionType.RETURN && transaction.returnTransactionId) {
      onTxnClick(transaction.returnTransactionId, transaction.returnStatus);
    } else if (onTxnClick) {
      onTxnClick(transaction.transactionId, transaction.transactionStatus);
    }
  };

  const getCardTitleText = (): string => {
    if (transaction.type === TransactionType.PAYMENT) {
      if (transaction.cardDetails?.network && transaction.cardDetails?.cardLast4) {
        return `${getCardType(transaction.cardDetails.network)} ****${transaction.cardDetails.cardLast4}`;
      }
      return getPaymentMethodNameForTxnCard(transaction.paymentMethod || '') || transaction.transactionId || '';
    }
    if (transaction.type === TransactionType.REFUND) {
      let text = 'Refund';
      if (transaction.cardDetails?.network && transaction.cardDetails?.cardLast4) {
        text += ` to ${getCardType(transaction.cardDetails.network)} ****${transaction.cardDetails.cardLast4}`;
      } else if (transaction.paymentMethod) {
        text += ` to ${transaction.paymentMethod || ''}`;
      }
      return text;
    }
    if (transaction.type === TransactionType.DISPUTE) {
      switch (transaction.disputeStatus) {
        case DISPUTE_STATUS.WON:
          return 'Dispute won';
        case DISPUTE_STATUS.LOST:
          return 'Dispute lost';
        case DISPUTE_STATUS.IN_REVIEW:
          return 'Dispute in review';
        case DISPUTE_STATUS.NEEDS_RESPONSE:
          return 'Dispute needs response';
        case DISPUTE_STATUS.NEEDS_ATTENTION:
          return 'Dispute needs attention';
        case DISPUTE_STATUS.PAST_DUE:
          return 'Dispute past due';
        default:
          return 'Dispute';
      }
    }
    if (transaction.type === TransactionType.RETURN) {
      return 'Check returned';
    }
    if (transaction.cardDetails?.network && transaction.cardDetails?.cardLast4) {
      return `${getCardType(transaction.cardDetails.network)} ****${transaction.cardDetails.cardLast4}`;
    }
    return PaymentMethodKeyNameLookup[transaction.paymentMethod] || transaction.transactionId || '';
  };

  const getFormattedDate = (): string => {
    const { refundAt, returnAt, disputeAt, transactionCreatedAt } = transaction;
    let date = transactionCreatedAt;
    if (refundAt) {
      date = refundAt;
    }
    if (returnAt) {
      date = returnAt;
    }
    if (disputeAt) {
      date = disputeAt;
    }
    return formatTransactionDate(date || transactionCreatedAt);
  };

  const getRightSideFirstLine = (): string => {
    if (transaction.type === TransactionType.PAYMENT) {
      return getAmountWithCurrency(transaction.invoicedAmount);
    }
    if (transaction.type === TransactionType.REFUND) {
      return `-${getAmountWithCurrency(transaction.refundAmount)}`;
    }
    if (transaction.type === TransactionType.DISPUTE) {
      return `-${getAmountWithCurrency(transaction.disputeAmount)}`;
    }
    if (transaction.type === TransactionType.RETURN) {
      return `-${getAmountWithCurrency(transaction.returnAmount)}`;
    }
    return transaction.invoicedAmount ? getAmountWithCurrency(transaction.invoicedAmount) : '';
  };

  const getRightSideSecondLine = (): string => {
    const { type, transactionStatus, tipAmount, refundStatus, returnStatus } = transaction;
    const hasTip = transactionStatus === TransactionStatus.SUCCESS && !!tipAmount;

    if (type === TransactionType.PAYMENT || typeof type === 'undefined') {
      return hasTip ? `+${getAmountWithCurrency(tipAmount)} Tip` : getReceiptStatus(transactionStatus);
    }
    if (type === TransactionType.REFUND) {
      return getReceiptStatus(refundStatus);
    }
    if (type === TransactionType.RETURN) {
      return getReceiptStatus(returnStatus);
    }

    return '';
  };

  const getStatusTextColor = (): string => {
    const { transactionStatus, refundStatus, returnStatus, type } = transaction;
    let status = transactionStatus;
    if (type === TransactionType.PAYMENT) {
      status = transactionStatus;
    }
    if (type === TransactionType.REFUND) {
      status = refundStatus;
    }
    if (type === TransactionType.RETURN) {
      status = returnStatus;
    }

    return getReceiptStatusColor(status || transactionStatus);
  };

  const Icon = getTxnCardIcon({
    status: transaction.transactionStatus as TransactionStatus,
    type: transaction.type,
    paymentMethod: transaction.paymentMethodName
  });

  return (
    <div
      className={`flex ${hasPermission ? 'cursor-pointer' : ''} justify-between py-2`}
      onClick={hasPermission ? handleCardClick : undefined}
      tabIndex={0}
      role="button"
      aria-label={`Transaction ${transaction.transactionId}`}>
      <div className="flex gap-2.5">
        <div
          className="relative flex h-10 w-10 shrink-0 items-center 
          justify-center rounded-md border border-borderGray">
          {Icon}
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-sbase font-semibold text-headingGray">{getCardTitleText()}</div>
          {transaction.type === TransactionType.DISPUTE && transaction.disputeReason && (
            <div className="max-w-[97%] truncate text-px13 font-medium text-accentText">
              {transaction.disputeReason}
            </div>
          )}
          <div className="text-px13 font-medium text-accentText">{getFormattedDate()}</div>
        </div>
      </div>
      <div className="flex gap-3">
        <div className="flex flex-col items-end gap-1">
          <div className="text-sbase font-semibold text-headingGray">{getRightSideFirstLine()}</div>
          <div className="text-px13 font-normal" style={{ color: getStatusTextColor() }}>
            {getRightSideSecondLine()}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-center">
          <ChevronArrowRightIcon className="path-fill-current text-gray-1" />
        </div>
      </div>
    </div>
  );
};

export default TransactionCardV2;
